"use client";
import { StaticImageData } from "next/image";
import ToolFeaturedArea, {
  THeroSectionInfoType,
} from "@/components/tools/ToolFeatured/ToolFeaturedArea";
import AdBanner from "@/components/Advertisement/AdBanner";
import ParaphraserCoreSection from "@/components/tools/ToolFunctional/paraphraser/ParaphraserCoreSection";
import CommonTitle from "@/components/common/CommonTitle";
import CommonSubtitle from "@/components/common/CommonSubtitle";
import SummarySection from "@/components/tools/ToolsCommon/SummarySection";
import ConclusionSection, {
  ConclusionSectionFeatureProps,
} from "@/components/tools/ToolsCommon/ConclusionSection";
import { PageInfoProps } from "../summarizer/root";
import TopInfoImageSection, {
  TopImageInfoType,
} from "@/components/tools/ToolsCommon/TopInfoImageSection";
import StepsSection, {
  StepsSectionFeatureProps,
} from "@/components/tools/ToolsCommon/StepsSection";
import ToolTopArea from "@/components/tools/ToolTop/ToolTopArea";
import AiToolsFaq, {
  IFaqList,
} from "@/components/tools/ToolsCommon/AiToolsFaq";
import AdvTools from "@/components/tools/AdvTools/AdvTools";

export type TAIPropTypes = {
  params: {
    pageInfo: PageInfoProps;
    heroSectionInfo: THeroSectionInfoType;
    conclusionSectionFeatures: ConclusionSectionFeatureProps[];
    stepsSectionFeatures: StepsSectionFeatureProps[];
    flexibilityList: TFeatureList[];
    faqList: IFaqList[];
    topInfoSectionFeature: TopImageInfoType;
  };
};

export type TFeatureList = {
  title: string;
  subtitle: string;
  featuredImg: StaticImageData;
};

const ParaphraserPageRoot = ({ params }: TAIPropTypes) => {
  const {
    pageInfo,
    heroSectionInfo,
    topInfoSectionFeature,
    flexibilityList,
    stepsSectionFeatures,
    conclusionSectionFeatures,
    faqList,
  } = params;

  return (
    <>
      <ToolTopArea pageInfo={pageInfo}>
        <div className="relative z-10 w-full">
          <ParaphraserCoreSection />
        </div>
      </ToolTopArea>
      <AdvTools
        title={<>Get Kroolo, The best of {" "}
          <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
            AI Paraphraser
          </span>
        </>
        }
        btnText="Try it free"
      />
      <div className="container mx-auto mb-24 max-w-[920px] space-y-3 text-center sm:space-y-4 md:space-y-5">
        <CommonTitle
          color="#202020"
          fontWeightClass="font-semibold"
          fontSizeClass="text-[22px] font-semibold xl:text-[28px]"
        >
          Why use Kroolo&apos;s paraphrasing tool?
        </CommonTitle>
        <CommonSubtitle
        >
          At Kroolo, we simplify writing with our AI paraphrasing tool. Save
          time while enhancing content for marketing, technical documents, or
          SEO. Write better and faster with our advanced AI solutions.
        </CommonSubtitle>
      </div>
      <TopInfoImageSection topInfoSection={topInfoSectionFeature} />
      <ToolFeaturedArea heroSectionInfo={heroSectionInfo} />
      <StepsSection
        featureArray={stepsSectionFeatures}
        title="Transform Writing with Kroolo's AI-Powered Paraphrasing Tool"
        subtitle="Kroolo offers an intuitive AI-powered paraphrasing tool that helps you improve your writing effortlessly. With just one click, our tool will rewrite sentences, paragraphs, reports, or articles to suit your needs, providing a variety of customization options to perfect your text."
      />
      <ConclusionSection
        title="Kroolo’s Paraphraser helps you write better, faster, and smarter"
        subtitle="Kroolo’s AI rewording tool is free and user-friendly. With a simple click, the paraphrasing tool can rephrase your sentence, paragraph, report, or article to match your preferences, offering plenty of options to customize and refine the reworded text."
        featureArray={conclusionSectionFeatures}
      />
      <SummarySection
        title="Empowering Every Writer"
        subtitle="Kroolo isn’t just about paraphrasing. We offer a few more AI-powered writing tools to help you in every aspect of writing and research. Ideal for product analysts, delivery managers, and anyone in between, Kroolo makes writing smarter and more efficient for all."
        featureList={flexibilityList}
      />
      <AiToolsFaq faqTitle="AI Paraphraser FAQs" faqList={faqList} />
      <AdBanner />
    </>
  );
};

export default ParaphraserPageRoot;
